// El borrador del agente de Santi dentro de una caja de escribir (entrega 2, propuesta §5.2): la caja de un contrato,
// la del chat con Santi o la de «Pedir algo». Reglas:
// - Llega un borrador y la caja está vacía (o tiene el borrador anterior sin tocar, o Pau pidió «Otra versión»): se pone
//   en la caja, y la caja lo dice («Borrador de tu agente · <porqué>»).
// - La caja tiene algo de Pau: NO se pisa. Sale «Tu agente tiene una respuesta preparada · Verla»; «Verla» lo cambia y deja
//   «Deshacer» 8 s (vuelve lo de Pau tal cual).
// - Pau edita el borrador: sigue siendo el borrador. Lo vacía del todo: se descarta (la caja vuelve a ser la de siempre).
// - Enviado o descartado, esa versión no vuelve a ponerse aunque la lista tarde en saberlo (`despachados`).
// Qué versión hay en la caja (su `en` y su texto tal como llegó) se guarda en el navegador junto al borrador de Pau, así
// que cambiar de contrato o recargar no pierde la etiqueta.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBorrador } from '../chat/borradores';
import { DURACION_LINEA_MS } from './store';

export interface BorradorEnCaja<T> {
  /** Identifica la versión (cada versión del agente, la suya). */
  en: string;
  valor: T;
  porque: string;
}

// Versiones ya enviadas o descartadas por caja: no se vuelven a poner (en memoria; la lista las quita enseguida).
const despachados = new Map<string, Set<string>>();
const yaDespachado = (clave: string, en: string) => despachados.get(clave)?.has(en) ?? false;
function despachar(clave: string, en: string | undefined) {
  if (!en) return;
  const s = despachados.get(clave) ?? new Set<string>();
  s.add(en);
  despachados.set(clave, s);
}

/** Para las pruebas. */
export function olvidarDespachados(): void {
  despachados.clear();
}

function leer<T>(marca: string): { en: string; valor: T } | null {
  if (!marca) return null;
  try {
    const m = JSON.parse(marca) as { en?: unknown; valor?: unknown };
    return typeof m.en === 'string' ? { en: m.en, valor: m.valor as T } : null;
  } catch {
    return null;
  }
}

export function useBorradorEnCaja<T>({
  clave,
  borrador,
  actual,
  poner,
  vacio,
  igual,
  alVaciar,
}: {
  /** La caja (la misma clave que su borrador de Pau: `santi:4`, `santi:general`, `santi:pedir`). */
  clave: string;
  borrador: BorradorEnCaja<T> | null;
  actual: T;
  poner: (v: T) => void;
  vacio: (v: T) => boolean;
  igual: (a: T, b: T) => boolean;
  /** Pau vació del todo el borrador: se descarta. */
  alVaciar: () => void;
}) {
  const [marca, setMarca] = useBorrador(`${clave}:agente`);
  const enCaja = leer<T>(marca);
  const [version, setVersion] = useState(false);
  const [deshacer, setDeshacer] = useState<{ previo: T; marca: string } | null>(null);
  const reloj = useRef<ReturnType<typeof setTimeout> | null>(null);
  const actualVacio = vacio(actual);
  const libre = actualVacio || (enCaja !== null && igual(actual, enCaja.valor)) || version;
  const pendiente = !!borrador && enCaja?.en !== borrador.en && !yaDespachado(clave, borrador.en);

  // Llega una versión nueva y la caja está libre: se pone.
  useEffect(() => {
    if (!borrador || !pendiente || !libre) return;
    poner(borrador.valor);
    setMarca(JSON.stringify({ en: borrador.en, valor: borrador.valor }));
    setVersion(false);
  }, [borrador, pendiente, libre, poner, setMarca]);

  // Pau vació el borrador (no al ponerlo: solo al pasar de algo a nada).
  const enBorrador = !!borrador && enCaja?.en === borrador.en && !actualVacio;
  const antes = useRef(enBorrador);
  useEffect(() => {
    if (antes.current && !enBorrador && actualVacio && borrador && enCaja?.en === borrador.en) {
      despachar(clave, borrador.en);
      setMarca('');
      alVaciar();
    }
    antes.current = enBorrador;
  }, [enBorrador, actualVacio, borrador, enCaja?.en, clave, setMarca, alVaciar]);

  useEffect(() => () => {
    if (reloj.current) clearTimeout(reloj.current);
  }, []);

  const verla = useCallback(() => {
    if (!borrador) return;
    setDeshacer({ previo: actual, marca });
    poner(borrador.valor);
    setMarca(JSON.stringify({ en: borrador.en, valor: borrador.valor }));
    if (reloj.current) clearTimeout(reloj.current);
    reloj.current = setTimeout(() => setDeshacer(null), DURACION_LINEA_MS);
  }, [borrador, actual, marca, poner, setMarca]);

  const deshacerVerla = useCallback(() => {
    if (!deshacer) return;
    poner(deshacer.previo);
    setMarca(deshacer.marca);
    setDeshacer(null);
    if (reloj.current) clearTimeout(reloj.current);
  }, [deshacer, poner, setMarca]);

  /** Enviado o descartado: esta versión ya no vuelve. */
  const soltar = useCallback(() => {
    despachar(clave, borrador?.en);
    despachar(clave, enCaja?.en);
    setMarca('');
    setDeshacer(null);
  }, [clave, borrador?.en, enCaja?.en, setMarca]);

  return {
    /** La caja enseña el borrador del agente (tono «tuyo», etiqueta, «Descartar», «Otra versión»). */
    enBorrador,
    porque: borrador?.porque ?? '',
    /** Hay una versión que no se puso porque la caja tiene algo de Pau: «Tu agente tiene una respuesta preparada · Verla». */
    hayOtro: pendiente && !libre && !deshacer,
    verla,
    /** Durante 8 s tras «Verla»: vuelve lo que había. */
    deshacer: deshacer ? deshacerVerla : null,
    soltar,
    /** «Otra versión»: la que llegue sustituye a la de ahora aunque Pau la haya tocado. */
    esperarVersion: useCallback(() => setVersion(true), []),
  };
}
